import { riskAssessmentResultConfigSchema } from '@risksmart-app/domain/src/types/risk-assessment-result-config';
import type { GetRiskAssessmentResultConfigVersionsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { isEqual } from 'lodash';
import type { ScoringMethodology, VersionHistoryEntry } from 'src/blocks';

type VersionRow =
  GetRiskAssessmentResultConfigVersionsQuery['risk_assessment_result_config'][number];

const getMethodology = (config: {
  impact: { categories: unknown[] };
}): ScoringMethodology =>
  config.impact.categories.length > 0 ? 'multi-impact' : 'impact-likelihood';

/**
 * Maps version-history query rows to the VersionHistoryEntry shape consumed by the
 * RiskScoringVersionHistory block. `isUpdated` is a deep InitialConfig-vs-Config
 * comparison, never a timestamp check — the demotion UPDATE bumps ModifiedAtTimestamp
 * on archived rows, which would falsely flag never-edited versions as updated.
 */
export const toVersionHistoryEntries = (
  rows: VersionRow[]
): VersionHistoryEntry[] =>
  [...rows]
    .sort((a, b) => b.Version - a.Version)
    .map((row) => {
      const config = riskAssessmentResultConfigSchema.parse(row.Config);

      const parsedInitial =
        row.InitialConfig != null
          ? riskAssessmentResultConfigSchema.parse(row.InitialConfig)
          : undefined;

      const isUpdated =
        parsedInitial != null && !isEqual(parsedInitial, config);

      return {
        id: row.Id,
        version: row.Version,
        isLatest: row.IsLatest,
        isUpdated,
        methodology: getMethodology(config),
        createdAt: row.CreatedAtTimestamp,
        createdByName: row.createdByUser?.FriendlyName ?? '',
        config,
        // "Fully effective from": the last in-place Config edit, or creation date if
        // never edited. ConfigModifiedAtTimestamp (unlike ModifiedAtTimestamp) isn't
        // bumped by the demotion, so it stays correct on archived rows.
        updatedAt: row.ConfigModifiedAtTimestamp,
        ...(isUpdated ? { initialConfig: parsedInitial } : {}),
      };
    });
